import { fetcher } from "@/lib/fetcher";
import type { ApiResponse } from "./model";

// Phase 1 skeleton: probe + discover only. tool/resource/prompt invocations land
// in later waves when api-agent integration is enabled. The add-mcp wizard only
// needs probe + discover today, so this file stays intentionally thin.
//
// NOTE: /v1/mcp/probe and /v1/mcp/discover do not exist yet — those endpoints
// arrive in W2. Calling these stubs in W1 will return 404 from the backend.

// The MCP OAuth redirect_uri ("Callback URL") users must register in their
// 3rd-party OAuth app. SINGLE SOURCE OF TRUTH: fetched from the backend (the
// executor owns it — it alone holds ConnectorBaseURL), NOT computed in the browser,
// so it can't drift from what the AS validates. The value is global + static per
// deployment, so the in-flight promise is memoized and reused for the session.
export interface MCPCallbackInfo {
  callback_url: string;
}

let mcpCallbackUrlPromise: Promise<string> | null = null;

export function getMcpOAuthCallbackUrl(): Promise<string> {
  if (!mcpCallbackUrlPromise) {
    mcpCallbackUrlPromise = fetcher<ApiResponse<MCPCallbackInfo[]>>(
      "/api/ai-transform/v1/mcp/oauth-callback-url",
    )
      .then((r) => r.data?.[0]?.callback_url ?? "")
      .catch((e) => {
        mcpCallbackUrlPromise = null; // allow a retry after a transient failure
        throw e;
      });
  }
  return mcpCallbackUrlPromise;
}

export interface MCPProbeResult {
  transport: "streamable_http" | "sse_legacy";
  auth_required: boolean;
  auth_hint?: string; // suggested auth_type (api_key | bearer_token | oauth2 | oauth2_cimd | oauth2_client_credentials | none)
  prm?: unknown; // OAuth Protected Resource Metadata (RFC 9728), opaque to UI
  as_metadata?: unknown; // OAuth Authorization Server Metadata (RFC 8414), opaque to UI
  // Probe-target verdict fields. On a classified FAILURE the backend rides a
  // Status=OK envelope (so the gateway never wraps it) with `transport` empty
  // and `error_code` set; probeMCP throws MCPProbeError before returning, so a
  // successfully-returned MCPProbeResult always has a real transport.
  reachable?: boolean;
  error_code?: string; // MCP_PROBE_HTTP_FORBIDDEN | MCP_PROBE_NOT_MCP | MCP_PROBE_TIMEOUT | MCP_PROBE_UNREACHABLE
  error_detail?: string;
  upstream_status?: number;
}

/**
 * Thrown by probeMCP when the server returns a classified probe-target failure
 * (unreachable / 403 / timeout / not-an-MCP). Carries the machine `code` so the
 * UI can map it to a friendly, localized message instead of showing the raw
 * gateway error.
 */
export class MCPProbeError extends Error {
  constructor(
    public readonly code: string,
    public readonly upstreamStatus?: number,
    message?: string,
  ) {
    super(message ?? code);
    this.name = "MCPProbeError";
  }
}

// Icon entry (MCP 2025-11-25) — {src, mime_type, sizes} for display in the UI.
export interface MCPIcon {
  src: string;
  mime_type?: string;
  sizes?: string[];
}

// Derived risk class for grouping read / write / delete and default policy.
export type MCPToolCategory = "read" | "write" | "destructive" | "unknown";

// Per-resource MCP annotations (spec 2025-11-25) — optional hints servers can
// surface for resources/templates. Tools have their own annotations shape
// (see MCPToolDef.annotations), so this is a separate type.
export interface MCPResourceAnnotations {
  audience?: string[];
  priority?: number;
  last_modified?: string;
}

export interface MCPToolDef {
  name: string;
  title?: string;
  description?: string;
  input_schema: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
  icons?: MCPIcon[];
  // category is derived server-side from annotations (read/write/destructive).
  category?: MCPToolCategory;
  annotations?: {
    title?: string;
    read_only_hint?: boolean;
    destructive_hint?: boolean;
    idempotent_hint?: boolean;
    open_world_hint?: boolean;
  };
  // Execution hints (MCP spec 2025-11-25). `task_support` describes how the
  // tool relates to long-running task primitives; UI is forward-compat only.
  execution?: {
    task_support?: "forbidden" | "optional" | "required";
  };
  // Free-form metadata bag (MCP spec 2025-11-25). Preserved on decode so we
  // don't drop fields the server sent; surfaced server-side for governance.
  _meta?: Record<string, unknown>;
}

export interface MCPResourceDef {
  uri: string;
  name: string;
  title?: string;
  description?: string;
  mime_type?: string;
  size?: number;
  icons?: MCPIcon[];
  annotations?: MCPResourceAnnotations;
  _meta?: Record<string, unknown>;
}

export interface MCPResourceTemplateDef {
  uri_template: string;
  name: string;
  title?: string;
  description?: string;
  mime_type?: string;
  icons?: MCPIcon[];
  annotations?: MCPResourceAnnotations;
  _meta?: Record<string, unknown>;
}

export interface MCPPromptDef {
  name: string;
  title?: string;
  description?: string;
  arguments?: Array<{
    name: string;
    title?: string;
    description?: string;
    required?: boolean;
  }>;
  icons?: MCPIcon[];
  _meta?: Record<string, unknown>;
}

// MCPAppDef — an MCP App per the apps extension
// (modelcontextprotocol.io/extensions/apps). An app is a `ui://` render surface:
// an HTML payload (rendered in a sandboxed iframe) that one or more tools target
// via their `_meta`. The backend folds both app signals into `type=ui` resource
// rows, so the FE derives apps from those rows — `id`/`resourceUri` are the ui://
// uri, `name`/`description` come off the resource row. Index stays loose for forward-compat.
export interface MCPAppDef {
  name: string;
  /** Declaring tool's name — stable identity for the app. */
  id?: string;
  description?: string;
  /** ui:// resource the tool's `_meta` references (the iframe payload). */
  resourceUri?: string;
  /**
   * Resource mime type — used to tell the ext-apps variant
   * (`text/html;profile=mcp-app`, renderable by @mcp-ui/client) apart from the
   * OpenAI Apps SDK variant (`text/html+skybridge`, needs ChatGPT's
   * `window.openai` host — we can't render it, so the UI degrades).
   */
  mimeType?: string;
  icons?: MCPIcon[];
  [key: string]: unknown;
}

/**
 * Read a tool's ext-apps UI render target from its `_meta` — the
 * `_meta.ui.resourceUri` (modelcontextprotocol.io/extensions/apps). Returns the
 * `ui://` uri when the tool declares an interactive UI surface, else undefined
 * (a plain tool). This is the linkage the app-render flow uses to pair a tool
 * with the widget HTML to render once the tool has produced a result.
 *
 * We deliberately read ONLY the ext-apps nested form, not the flat OpenAI
 * `openai/outputTemplate` — that one points at the `text/html+skybridge`
 * variant which our host can't drive (see McpAppWidget).
 */
export function toolUiResourceUri(
  meta?: Record<string, unknown>,
): string | undefined {
  const ui = meta?.ui;
  if (ui && typeof ui === "object") {
    const uri = (ui as { resourceUri?: unknown }).resourceUri;
    if (typeof uri === "string" && uri) return uri;
  }
  return undefined;
}

// MCPServerInfo — the initialize.result.serverInfo shape (MCP spec
// 2025-11-25). `title` is the human-readable display name; `description` and
// `website_url` are 2025-11-25 additions. `icons` ride alongside per spec.
// Reused on both the live `MCPDiscoverResult.server_info` and the persisted
// mcps row config (`MCPConfig.server_info` via the zod schema).
export interface MCPServerInfo {
  name: string;
  version: string;
  title?: string;
  description?: string;
  website_url?: string;
  icons?: MCPIcon[];
}

export interface MCPDiscoverResult {
  tools: MCPToolDef[];
  resources: MCPResourceDef[];
  resource_templates: MCPResourceTemplateDef[];
  prompts: MCPPromptDef[];
  // apps: optional, forward-compat for SEP-1865; servers that don't advertise
  // it omit the field entirely (or send []).
  apps?: MCPAppDef[];
  capabilities?: unknown;
  server_info?: MCPServerInfo;
  // instructions: optional markdown text from initialize.result.instructions
  // (MCP spec ≥2025-06-18). Servers use it to surface hints/context in the UI.
  instructions?: string;
  protocol_version?: string;
  // Stage 1 per-primitive discover errors (present when a list call failed; the
  // discover envelope itself still rides Status=OK). *_error_code is the routing
  // category (MCP_FORBIDDEN / MCP_UNAVAILABLE / MCP_PROTOCOL / MCP_SERVER_ERROR / NEEDS_AUTH).
  tools_error?: string;
  tools_error_code?: string;
  resources_error?: string;
  resources_error_code?: string;
  resource_templates_error?: string;
  resource_templates_error_code?: string;
  prompts_error?: string;
  prompts_error_code?: string;
}

/**
 * Probe an MCP server URL to detect transport + auth requirements.
 * Called by the add-mcp wizard on URL paste to suggest auth_type.
 *
 * Backend: POST /v1/mcp/probe { url } — proxied via /api/ai-transform.
 * Available from W2 onward; returns 404 until then.
 */
export async function probeMCP(url: string): Promise<MCPProbeResult> {
  const response = await fetcher<ApiResponse<MCPProbeResult[]>>(
    "/api/ai-transform/v1/mcp/probe",
    {
      method: "POST",
      body: JSON.stringify({ url }),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    const result = response.data[0];
    // Always-OK contract: a classified probe-target failure also arrives as
    // Status=OK with error_code set (so the upstream gateway never wraps it).
    // Surface it as a typed error the wizard maps to a friendly message, rather
    // than returning a hollow Detection (transport would be "").
    if (result.error_code) {
      throw new MCPProbeError(
        result.error_code,
        result.upstream_status,
        result.error_detail,
      );
    }
    return result;
  }

  // Genuine non-OK envelope (executor-internal 500 / EXECUTOR_RETURNED_NON_JSON).
  throw new Error(response.message || "Failed to probe MCP server");
}

/**
 * Discover the tool/resource/prompt catalog of an MCP server.
 * Called by the capability preview page after connect.
 *
 * Backend: POST /v1/mcp/discover { mcp_id, force_refresh? } — proxied via /api/ai-transform.
 * Available from W2 onward; returns 404 until then.
 */
export async function discoverMCP(
  mcpId: string,
  forceRefresh = false,
): Promise<MCPDiscoverResult> {
  const response = await fetcher<ApiResponse<MCPDiscoverResult[]>>(
    "/api/ai-transform/v1/mcp/discover",
    {
      method: "POST",
      body: JSON.stringify({ mcp_id: mcpId, force_refresh: forceRefresh }),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    return response.data[0];
  }

  if (response.error_code) {
    throw new MCPConnectError(response.error_code, response.message);
  }
  throw new Error(response.message || "Failed to discover MCP capabilities");
}

/**
 * Returns the first per-primitive discover error (code + verbatim backend detail),
 * or null when every primitive loaded. Order: tools → resources → templates → prompts.
 * Used by the reconnect flow to surface why the catalog is empty.
 */
export function firstDiscoverError(
  r: MCPDiscoverResult,
): { code?: string; detail?: string } | null {
  const pairs: Array<[string | undefined, string | undefined]> = [
    [r.tools_error_code, r.tools_error],
    [r.resources_error_code, r.resources_error],
    [r.resource_templates_error_code, r.resource_templates_error],
    [r.prompts_error_code, r.prompts_error],
  ];
  for (const [code, detail] of pairs) {
    if (code || detail) return { code, detail };
  }
  return null;
}

// W3.T3.11: connect endpoint. Triggers a probe + (if OAuth) returns the
// authorization URL the wizard should open in a popup; for static auth it
// returns connected immediately. tools/call, resources/*, prompts/* land in W4+.
export type MCPConnectResponse =
  | { status: "connected" }
  | { status: "needs_oauth"; authorization_url: string; state: string };

/**
 * Thrown by connectMCP when the envelope carries a classified failure. `code`
 * is the MCP_CONNECT_* (or EXECUTOR_* transport) error_code from
 * api-ai-transform — api-side set in its model/error_codes.go, executor-side
 * contract in docs/mcp-connector/checklist.md. The UI maps codes to localized
 * messages via lib/mcp-connect-error.ts; `message` keeps the backend
 * diagnostic as fallback.
 */
export class MCPConnectError extends Error {
  constructor(
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = "MCPConnectError";
  }
}

/**
 * Trigger executor `/v1/mcp/connect` for a saved MCP System. The executor
 * loads the AuthConfig, resolves the auth strategy and either:
 *  - returns { status: "connected" } when no user interaction is needed
 *    (static credentials, already-stored OAuth refresh token, etc.)
 *  - returns { status: "needs_oauth", authorization_url, state } when the
 *    wizard must open the auth URL in a popup. After the user grants
 *    consent the OAuth callback page (T3.10) postMessages back.
 */
export async function connectMCP(
  mcpId: string,
): Promise<MCPConnectResponse> {
  const response = await fetcher<ApiResponse<MCPConnectResponse[]>>(
    "/api/ai-transform/v1/mcp/connect",
    {
      method: "POST",
      body: JSON.stringify({ mcp_id: mcpId }),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    return response.data[0];
  }

  if (response.error_code) {
    throw new MCPConnectError(response.error_code, response.message);
  }
  throw new Error(response.message || "Failed to connect MCP server");
}

// NOTE: MCP config edits no longer go through System. The MCP server is a
// first-class entity — use updateMcp / updateMcpConfig from
// mcps-service.ts (PUT /v1/mcp) to persist config changes.

// ============================================================================
// Preview — lazy /v1/mcp/resources/preview + /v1/mcp/prompts/preview endpoints.
// Both fetch live via the executor (no server-side cache). The resource preview
// uploads the content to file storage and returns a lightweight `link` the FE
// fetches the content from; the prompt preview returns the render inline.
// ============================================================================

export interface MCPResourcePreview {
  uri: string;
  mime_type?: string;
  link?: string; // viewURL the FE fetches the content from
  size_bytes?: number;
  fetched_at?: string; // RFC 3339
}

export interface MCPPromptPreview {
  prompt_name: string;
  description?: string;
  messages?: unknown[]; // PromptMessage[] verbatim; renderer downstream walks .content
  fetched_at?: string; // RFC 3339
}

/**
 * Preview a single MCP resource. The api fetches the content live via the
 * executor, uploads it to file storage, and returns a `link` (viewURL) the
 * caller fetches the content from.
 *
 * Backend: POST /v1/mcp/resources/preview { mcp_id, uri }
 */
export async function previewMCPResource(
  mcpId: string,
  uri: string,
): Promise<MCPResourcePreview> {
  const response = await fetcher<ApiResponse<MCPResourcePreview[]>>(
    "/api/ai-transform/v1/mcp/resources/preview",
    {
      method: "POST",
      body: JSON.stringify({ mcp_id: mcpId, uri }),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    return response.data[0];
  }

  throw new Error(response.message || "Failed to preview MCP resource");
}

/**
 * Preview a single MCP prompt render. The api fetches the render live via the
 * executor and returns description + messages inline.
 *
 * Backend: POST /v1/mcp/prompts/preview { mcp_id, name, arguments? }
 */
export async function previewMCPPrompt(
  mcpId: string,
  name: string,
  args?: Record<string, unknown>,
): Promise<MCPPromptPreview> {
  const body: Record<string, unknown> = { mcp_id: mcpId, name };
  if (args && Object.keys(args).length > 0) {
    body.arguments = args;
  }

  const response = await fetcher<ApiResponse<MCPPromptPreview[]>>(
    "/api/ai-transform/v1/mcp/prompts/preview",
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    return response.data[0];
  }

  throw new Error(response.message || "Failed to preview MCP prompt");
}

// ============================================================================
// Tool invocation — POST /v1/mcp/tools/call
// ============================================================================

/**
 * Content block returned by a tool call (MCP spec 2025-11-25 §6.2 — same
 * union the executor proxies verbatim). Image / audio carry base64 bytes,
 * resource embeds the resource preview, resource_link is a reference.
 */
export type MCPToolContentBlock =
  | { type: "text"; text: string; _meta?: Record<string, unknown> }
  | {
      type: "image";
      data: string;
      mime_type: string;
      _meta?: Record<string, unknown>;
    }
  | {
      type: "audio";
      data: string;
      mime_type: string;
      _meta?: Record<string, unknown>;
    }
  | {
      type: "resource";
      resource: {
        uri: string;
        mime_type?: string;
        text?: string;
        blob?: string;
      };
      _meta?: Record<string, unknown>;
    }
  | {
      type: "resource_link";
      uri: string;
      name?: string;
      description?: string;
      mime_type?: string;
      _meta?: Record<string, unknown>;
    };

export interface MCPToolCallResult {
  content: MCPToolContentBlock[];
  /** Optional structured payload alongside the content blocks. */
  structured_content?: Record<string, unknown>;
  /** True when the executor signalled the tool itself failed (vs. transport). */
  is_error?: boolean;
  /** Wall-clock duration the executor measured, if surfaced. */
  duration_ms?: number;
}

/**
 * Execute an MCP tool. Calls are never cached client-side — each invocation
 * is a fresh side-effect, especially for write/destructive tools, so we use
 * a plain POST and return the result directly.
 *
 * Wire shape: the executor's MCPRequest expects `params: {name, arguments}` —
 * api-ai-transform forwards the body verbatim through HandleMCPGeneric, so the
 * `name` + `arguments` MUST sit inside `params` (not at the top level) or the
 * executor's tools/call dispatcher sees `Params: nil` and calls CallTool("", nil).
 *
 * Response shape: api is pass-through here (no stamping like discover), so the
 * executor's camelCase wire (`isError`, `mimeType`, `structuredContent` —
 * matching MCP spec 2025-11-25) lands on the FE as-is. We normalise to the
 * snake_case the rest of the FE types use so downstream renderers don't have
 * to branch on case.
 *
 * Backend: POST /v1/mcp/tools/call { mcp_id, params: { name, arguments? } }
 */
export async function callMCPTool(
  mcpId: string,
  name: string,
  args?: Record<string, unknown>,
): Promise<MCPToolCallResult> {
  const params: Record<string, unknown> = { name };
  if (args && Object.keys(args).length > 0) {
    params.arguments = args;
  }
  const body = { mcp_id: mcpId, params };

  // Decode as a loose record first because the executor returns camelCase
  // (MCP spec wire) but our typed MCPToolCallResult is snake_case.
  const response = await fetcher<ApiResponse<Record<string, unknown>[]>>(
    "/api/ai-transform/v1/mcp/tools/call",
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    return normaliseCallToolResult(response.data[0]);
  }

  if (response.error_code) {
    throw new MCPConnectError(response.error_code, response.message);
  }
  throw new Error(response.message || "Failed to call MCP tool");
}

/**
 * Execute an MCP tool and return the executor's response VERBATIM (camelCase:
 * `content`, `isError`, `structuredContent`, `_meta`) — i.e. the MCP SDK
 * `CallToolResult` shape, without the snake_case normalisation `callMCPTool`
 * applies. This is what the MCP Apps bridge (`@mcp-ui/client` AppRenderer's
 * `onCallTool`) expects when a rendered widget calls a tool back, so we hand it
 * the raw shape rather than re-converting snake → camel.
 *
 * Backend: POST /v1/mcp/tools/call { mcp_id, params: { name, arguments? } }
 */
export async function callMCPToolRaw(
  mcpId: string,
  name: string,
  args?: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const params: Record<string, unknown> = { name };
  if (args && Object.keys(args).length > 0) {
    params.arguments = args;
  }
  const response = await fetcher<ApiResponse<Record<string, unknown>[]>>(
    "/api/ai-transform/v1/mcp/tools/call",
    {
      method: "POST",
      body: JSON.stringify({ mcp_id: mcpId, params }),
    },
  );

  if (response.status === "OK" && response.data?.[0]) {
    return response.data[0];
  }
  if (response.error_code) {
    throw new MCPConnectError(response.error_code, response.message);
  }
  throw new Error(response.message || "Failed to call MCP tool");
}

/**
 * Map the executor's camelCase tools/call response keys to the snake_case
 * field names the rest of the FE expects. Walks each content block to
 * convert `mimeType` → `mime_type` (top-level and on embedded resources).
 */
function normaliseCallToolResult(raw: Record<string, unknown>): MCPToolCallResult {
  const content = Array.isArray(raw.content) ? raw.content : [];
  return {
    content: content.map(normaliseContentBlock) as MCPToolContentBlock[],
    structured_content:
      (raw.structuredContent as Record<string, unknown> | undefined) ??
      (raw.structured_content as Record<string, unknown> | undefined),
    is_error: Boolean(raw.isError ?? raw.is_error),
    duration_ms: raw.duration_ms as number | undefined,
  };
}

function normaliseContentBlock(block: unknown): unknown {
  if (!block || typeof block !== "object") return block;
  const b = block as Record<string, unknown>;
  const out: Record<string, unknown> = { ...b };
  if (b.mimeType !== undefined && out.mime_type === undefined) {
    out.mime_type = b.mimeType;
    delete out.mimeType;
  }
  if (b.resource && typeof b.resource === "object") {
    const r = b.resource as Record<string, unknown>;
    const resOut: Record<string, unknown> = { ...r };
    if (r.mimeType !== undefined && resOut.mime_type === undefined) {
      resOut.mime_type = r.mimeType;
      delete resOut.mimeType;
    }
    out.resource = resOut;
  }
  return out;
}

export interface McpInstructionResult {
  matched: boolean;
  /** A doc/guide URL the user can open — not a markdown body. */
  instruction?: string;
  match_type?: string;
  match_value?: string;
}

/**
 * Resolve a per-app setup instruction for an MCP endpoint address. The
 * resolved `instruction` is a URL (a doc/guide link the user can open), not a
 * markdown body — the surrounding copy is static and generic on the client.
 * Best-effort: any failure or no-match resolves to { matched: false } — this
 * must never throw into the onboarding flow.
 *
 * Backend: QUERY /v1/instruction { search: <address> } — the address rides in
 * the standard common.Query Search field. Proxied via /api/ai-transform.
 */
export async function resolveMcpInstruction(
  address: string,
): Promise<McpInstructionResult> {
  try {
    const response = await fetcher<ApiResponse<McpInstructionResult[]>>(
      `/api/ai-transform/v1/instruction`,
      { method: "QUERY", body: JSON.stringify({ search: address }) },
    );
    if (response.status === "OK" && response.data?.[0]) {
      return response.data[0];
    }
    return { matched: false };
  } catch {
    return { matched: false };
  }
}
